/* eslint-disable @typescript-eslint/no-explicit-any -- react-hook-form useFieldArray name types require any */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { occupationApi, skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { LoadingState, ErrorState } from '@/components/common';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import type { Occupation, Skill, CareerLevel, OccupationType, OccupationStatus, GrowthOutlook } from '@/types/skillKnowledge';

const CAREER_LEVELS: CareerLevel[] = ['ENTRY_LEVEL', 'JUNIOR', 'MID_LEVEL', 'SENIOR', 'LEAD', 'PRINCIPAL', 'DIRECTOR', 'VP', 'C_LEVEL', 'EXECUTIVE'];
const OCCUPATION_TYPES: OccupationType[] = ['INDIVIDUAL_CONTRIBUTOR', 'TEAM_LEAD', 'MANAGER', 'DIRECTOR', 'VICE_PRESIDENT', 'C_LEVEL', 'EXECUTIVE', 'SELF_EMPLOYED', 'CONSULTANT', 'CONTRACTOR', 'FREELANCER', 'ENTREPRENEUR'];
const STATUSES: OccupationStatus[] = ['DRAFT', 'ACTIVE', 'UNDER_REVIEW', 'ARCHIVED'];
const GROWTH_OUTLOOKS: GrowthOutlook[] = ['DECLINING', 'STABLE', 'GROWING', 'RAPIDLY_GROWING', 'EMERGING'];
const CATEGORIES = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'TEMPORARY', 'INTERN', 'VOLUNTEER', 'FREELANCE', 'SEASONAL', 'OTHER'] as const;
const INDUSTRIES = ['TECHNOLOGY', 'HEALTHCARE', 'FINANCE', 'EDUCATION', 'MANUFACTURING', 'RETAIL', 'HOSPITALITY', 'CONSTRUCTION', 'TRANSPORTATION', 'GOVERNMENT', 'NON_PROFIT', 'OTHER'] as const;
const REQUIRED_SKILL_LEVELS = ['FOUNDATIONAL', 'INTERMEDIATE', 'ADVANCED', 'EXPERT'] as const; // API requirement: no BASIC or MASTERY

const requiredSkillSchema = z.object({
  skillId: z.string(), // Allow empty strings - will be filtered out before submission
  proficiencyLevel: z.enum(['FOUNDATIONAL', 'INTERMEDIATE', 'ADVANCED', 'EXPERT']), // API requirement: no BASIC or MASTERY
  isRequired: z.boolean().optional(),
});

const occupationSchema = z.object({
  title: z.string().min(2, 'Title must be at least 2 characters').max(200, 'Title must be less than 200 characters'),
  alternativeTitles: z.array(z.string()).optional(),
  description: z.string().min(10, 'Description must be at least 10 characters').max(3000, 'Description must be less than 3000 characters'), // API maxLength: 3000
  summary: z.string().max(500, 'Summary must be less than 500 characters').optional(),
  category: z.enum(['FULL_TIME', 'PART_TIME', 'CONTRACT', 'TEMPORARY', 'INTERN', 'VOLUNTEER', 'FREELANCE', 'SEASONAL', 'OTHER']), // Required by API
  industry: z.enum(['TECHNOLOGY', 'HEALTHCARE', 'FINANCE', 'EDUCATION', 'MANUFACTURING', 'RETAIL', 'HOSPITALITY', 'CONSTRUCTION', 'TRANSPORTATION', 'GOVERNMENT', 'NON_PROFIT', 'OTHER']), // Required by API
  careerLevel: z.enum(['ENTRY_LEVEL', 'JUNIOR', 'MID_LEVEL', 'SENIOR', 'LEAD', 'PRINCIPAL', 'DIRECTOR', 'VP', 'C_LEVEL', 'EXECUTIVE']),
  occupationType: z.enum(['INDIVIDUAL_CONTRIBUTOR', 'TEAM_LEAD', 'MANAGER', 'DIRECTOR', 'VICE_PRESIDENT', 'C_LEVEL', 'EXECUTIVE', 'SELF_EMPLOYED', 'CONSULTANT', 'CONTRACTOR', 'FREELANCER', 'ENTREPRENEUR']),
  status: z.enum(['DRAFT', 'ACTIVE', 'UNDER_REVIEW', 'ARCHIVED']).optional(),
  isActive: z.boolean().optional(),
  requiredSkills: z.array(requiredSkillSchema).optional(),
  optionalSkills: z.array(requiredSkillSchema).optional(),
  experienceRequired: z.number().min(0).optional(),
  educationRequired: z.array(z.string()).optional(),
  growthOutlook: z.enum(['DECLINING', 'STABLE', 'GROWING', 'RAPIDLY_GROWING', 'EMERGING']).optional(),
  popularityScore: z.number().min(0).max(100).optional(),
});

type OccupationFormData = z.infer<typeof occupationSchema>;

export const OccupationFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [skills, setSkills] = useState<Skill[]>([]);

  const form = useForm<OccupationFormData>({
    resolver: zodResolver(occupationSchema),
      defaultValues: {
        title: '',
        alternativeTitles: [],
        description: '',
        summary: '',
        category: 'FULL_TIME', // Required by API
        industry: 'TECHNOLOGY', // Required by API
        careerLevel: 'MID_LEVEL',
        occupationType: 'INDIVIDUAL_CONTRIBUTOR',
        status: 'DRAFT',
        isActive: true,
        requiredSkills: [],
        optionalSkills: [],
        experienceRequired: undefined,
        educationRequired: [],
        growthOutlook: undefined,
        popularityScore: undefined,
      },
  });

  const { fields: alternativeTitleFields, append: appendAlternativeTitle, remove: removeAlternativeTitle } = useFieldArray({
    control: form.control,
    name: 'alternativeTitles' as any,
  });

  const { fields: requiredSkillFields, append: appendRequiredSkill, remove: removeRequiredSkill } = useFieldArray({
    control: form.control,
    name: 'requiredSkills',
  });

  const { fields: optionalSkillFields, append: appendOptionalSkill, remove: removeOptionalSkill } = useFieldArray({
    control: form.control,
    name: 'optionalSkills',
  });

  const { fields: educationFields, append: appendEducation, remove: removeEducation } = useFieldArray({
    control: form.control,
    name: 'educationRequired' as any,
  });

  useEffect(() => {
    // Load skills for dropdown
    skillApi.list({ page: 0, size: 1000 }).then((response) => {
      setSkills(response.content || []);
    }).catch((error) => {
      console.error('Error loading skills:', error);
    });
  }, []);

  // Fetch occupation data for edit mode
  const {
    data: _occupation,
    isLoading: isLoadingOccupation,
    error: occupationError,
  } = useEntity<Occupation>({
    fetchFn: (occupationId: string) => occupationApi.getById(occupationId),
    entityId: id,
    entityName: 'occupation',
    enabled: isEdit && !!id,
    onSuccess: (data) => {
      form.reset({
        title: data.title,
        alternativeTitles: data.alternativeTitles || [],
        description: data.description,
        summary: data.summary || '',
        category: (data.category || 'FULL_TIME') as OccupationFormData['category'], // Required by API
        industry: (data.industry || 'TECHNOLOGY') as OccupationFormData['industry'], // Required by API
        careerLevel: data.careerLevel,
        occupationType: data.occupationType as OccupationType,
        status: (data.status || 'DRAFT') as OccupationFormData['status'],
        isActive: data.isActive ?? true,
        requiredSkills: data.requiredSkills || [],
        optionalSkills: data.optionalSkills || [],
        experienceRequired: data.experienceRequired,
        educationRequired: data.educationRequired || [],
        growthOutlook: data.growthOutlook as OccupationFormData['growthOutlook'],
        popularityScore: data.popularityScore,
      } as OccupationFormData);
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: OccupationFormData) => {
      const payload: Omit<Occupation, 'id' | 'createdAt' | 'updatedAt'> = {
        title: data.title,
        alternativeTitles: data.alternativeTitles?.filter(Boolean),
        description: data.description,
        summary: data.summary,
        category: data.category, // Required by API
        industry: data.industry, // Required by API
        careerLevel: data.careerLevel,
        occupationType: data.occupationType as OccupationType,
        status: data.status,
        isActive: data.isActive ?? true,
        requiredSkills: data.requiredSkills?.filter((s) => s.skillId && s.skillId !== 'none'),
        optionalSkills: data.optionalSkills?.filter((s) => s.skillId && s.skillId !== 'none'),
        experienceRequired: data.experienceRequired,
        educationRequired: data.educationRequired?.filter(Boolean),
        growthOutlook: data.growthOutlook || undefined,
        popularityScore: data.popularityScore,
      };
      return occupationApi.create(payload);
    },
    entityName: 'occupation',
    operation: 'create',
    onSuccess: () => {
      navigate('/admin/occupations');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: OccupationFormData) => {
      if (!id) throw new Error('Occupation ID is required for update');
      const payload: Partial<Occupation> = {
        title: data.title,
        alternativeTitles: data.alternativeTitles?.filter(Boolean),
        description: data.description,
        summary: data.summary,
        category: data.category, // Required by API
        industry: data.industry, // Required by API
        careerLevel: data.careerLevel,
        occupationType: data.occupationType as OccupationType,
        status: data.status,
        isActive: data.isActive ?? true,
        requiredSkills: data.requiredSkills?.filter((s) => s.skillId && s.skillId !== 'none'),
        optionalSkills: data.optionalSkills?.filter((s) => s.skillId && s.skillId !== 'none'),
        experienceRequired: data.experienceRequired,
        educationRequired: data.educationRequired?.filter(Boolean),
        growthOutlook: data.growthOutlook || undefined,
        popularityScore: data.popularityScore,
      };
      return occupationApi.update(id, payload);
    },
    entityName: 'occupation',
    operation: 'update',
    onSuccess: () => {
      navigate('/admin/occupations');
    },
  });

  const onSubmit = async (data: OccupationFormData) => {
    if (isEdit) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const isLoading = isLoadingOccupation;
  const mutation = isEdit ? updateMutation : createMutation;

  // In edit mode, show loading state while fetching
  if (isEdit && isLoading) {
    return <LoadingState message="Loading occupation..." />;
  }

  // Show error if there's an error (only after loading completes)
  if (isEdit && occupationError) {
    return <ErrorState error={occupationError} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/occupations">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{isEdit ? 'Edit Occupation' : 'Create Occupation'}</h1>
          <p className="text-muted-foreground mt-2">{isEdit ? 'Update occupation details' : 'Add a new occupation'}</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
          console.error('Form validation errors:', errors);
        })} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Enter the basic details for this occupation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Software Engineer" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <Label>Alternative Titles</Label>
                <div className="space-y-2">
                  {alternativeTitleFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Input
                        placeholder="Enter alternative title"
                        value={form.watch(`alternativeTitles.${index}`) || ''}
                        onChange={(e) => {
                          const current = form.getValues('alternativeTitles') || [];
                          current[index] = e.target.value;
                          form.setValue('alternativeTitles', current);
                        }}
                      />
                      <Button type="button" variant="outline" size="icon" onClick={() => removeAlternativeTitle(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => appendAlternativeTitle('')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Alternative Title
                  </Button>
                </div>
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Enter detailed description" className="min-h-[120px]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="summary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Summary</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Enter brief summary" {...field} />
                    </FormControl>
                    <FormDescription>Brief summary of the occupation</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Industry *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select industry" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {INDUSTRIES.map((ind) => (
                            <SelectItem key={ind} value={ind}>
                              {ind.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="careerLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Career Level *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select career level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CAREER_LEVELS.map((level) => (
                            <SelectItem key={level} value={level}>
                              {level.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="occupationType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Occupation Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select occupation type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {OCCUPATION_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="growthOutlook"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Growth Outlook</FormLabel>
                      <Select 
                        onValueChange={(value) => field.onChange(value === 'none' ? undefined : (value as GrowthOutlook))} 
                        value={field.value || 'none'}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select growth outlook" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {GROWTH_OUTLOOKS.map((outlook) => (
                            <SelectItem key={outlook} value={outlook}>
                              {outlook.replace(/_/g, ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="experienceRequired"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Experience Required (Years)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          placeholder="Enter years"
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="popularityScore"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Popularity Score (0-100)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          placeholder="Enter score"
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label>Education Required</Label>
                <div className="space-y-2">
                  {educationFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Input
                        placeholder="Enter education requirement"
                        value={form.watch(`educationRequired.${index}`) || ''}
                        onChange={(e) => {
                          const current = form.getValues('educationRequired') || [];
                          current[index] = e.target.value;
                          form.setValue('educationRequired', current);
                        }}
                      />
                      <Button type="button" variant="outline" size="icon" onClick={() => removeEducation(index)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => appendEducation('')}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Education Requirement
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Required Skills</CardTitle>
              <CardDescription>Skills that are required for this occupation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {requiredSkillFields.map((field, index) => (
                <div key={field.id} className="grid gap-4 md:grid-cols-2">
                  <Select
                    value={form.watch(`requiredSkills.${index}.skillId`) || 'none'}
                    onValueChange={(value) => {
                      const current = form.getValues('requiredSkills') || [];
                      current[index] = { ...current[index], skillId: value === 'none' ? '' : value };
                      form.setValue('requiredSkills', current);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select skill" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {skills.map((skill) => (
                        <SelectItem key={skill.id} value={skill.id || ''}>
                          {skill.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Select
                      value={form.watch(`requiredSkills.${index}.proficiencyLevel`) || 'FOUNDATIONAL'}
                      onValueChange={(value) => {
                        const current = form.getValues('requiredSkills') || [];
                        current[index] = { ...current[index], proficiencyLevel: value as typeof REQUIRED_SKILL_LEVELS[number] };
                        form.setValue('requiredSkills', current);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {REQUIRED_SKILL_LEVELS.map((level) => (
                          <SelectItem key={level} value={level}>
                            {level}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="icon" onClick={() => removeRequiredSkill(index)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => appendRequiredSkill({ skillId: '', proficiencyLevel: 'FOUNDATIONAL' } as any)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Required Skill
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Optional Skills</CardTitle>
              <CardDescription>Skills that are beneficial but not required</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {optionalSkillFields.map((field, index) => (
                <div key={field.id} className="grid gap-4 md:grid-cols-2">
                  <Select
                    value={form.watch(`optionalSkills.${index}.skillId`) || 'none'}
                    onValueChange={(value) => {
                      const current = form.getValues('optionalSkills') || [];
                      current[index] = { ...current[index], skillId: value === 'none' ? '' : value };
                      form.setValue('optionalSkills', current);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select skill" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {skills.map((skill) => (
                        <SelectItem key={skill.id} value={skill.id || ''}>
                          {skill.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Select
                      value={form.watch(`optionalSkills.${index}.proficiencyLevel`) || 'FOUNDATIONAL'}
                      onValueChange={(value) => {
                        const current = form.getValues('optionalSkills') || [];
                        current[index] = { ...current[index], proficiencyLevel: value as typeof REQUIRED_SKILL_LEVELS[number] };
                        form.setValue('optionalSkills', current);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select level" />
                      </SelectTrigger>
                      <SelectContent>
                        {REQUIRED_SKILL_LEVELS.map((level) => (
                          <SelectItem key={level} value={level}>
                            {level}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" variant="outline" size="icon" onClick={() => removeOptionalSkill(index)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => appendOptionalSkill({ skillId: '', proficiencyLevel: 'FOUNDATIONAL' } as any)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Optional Skill
              </Button>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/admin/occupations">Cancel</Link>
            </Button>
            <Button type="submit" disabled={mutation.isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {mutation.isLoading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default OccupationFormPage;






